<!-- src/views/appointments/AppointmentList.vue -->
<template>
  <div class="appointment-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>Citas</h1>
      <router-link to="/appointments/new" class="btn btn-primary">
        <i class="bi bi-plus-circle me-1"></i> Nueva Cita
      </router-link>
    </div>

    <!-- Filters and search bar -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <!-- Search by patient name -->
          <div class="col-md-4">
            <div class="input-group">
              <span class="input-group-text" id="search-addon">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Buscar paciente..."
                v-model="filters.search"
                @input="applyFilters"
              />
            </div>
          </div>

          <!-- Filter by date range -->
          <div class="col-md-3">
            <input
              type="date"
              class="form-control"
              placeholder="Fecha desde"
              v-model="filters.dateFrom"
              @change="applyFilters"
            />
          </div>
          <div class="col-md-3">
            <input
              type="date"
              class="form-control"
              placeholder="Fecha hasta"
              v-model="filters.dateTo"
              @change="applyFilters"
            />
          </div>

          <!-- Filter by status -->
          <div class="col-md-2">
            <select class="form-select" v-model="filters.status" @change="applyFilters">
              <option value="">Todos los estados</option>
              <option value="programada">Programada</option>
              <option value="completada">Completada</option>
              <option value="inasistencia">Inasistencia</option>
              <option value="confirmada">Confirmada</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Calendar/List view toggle -->
    <div class="card mb-4">
      <div class="card-body p-2">
        <div class="btn-group w-100">
          <button 
            type="button" 
            class="btn btn-outline-primary" 
            :class="{ active: viewMode === 'list' }"
            @click="viewMode = 'list'"
          >
            <i class="bi bi-list-ul me-1"></i> Lista
          </button>
          <button 
            type="button" 
            class="btn btn-outline-primary" 
            :class="{ active: viewMode === 'calendar' }"
            @click="viewMode = 'calendar'"
          >
            <i class="bi bi-calendar-week me-1"></i> Calendario
          </button>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      Error al cargar las citas: {{ error }}
    </div>

    <!-- Empty state -->
    <div v-else-if="filteredAppointments.length === 0" class="text-center my-5">
      <div class="display-1 text-muted mb-3">
        <i class="bi bi-calendar-x"></i>
      </div>
      <h3 class="text-muted">No se encontraron citas</h3>
      <p v-if="hasActiveFilters">No hay citas que coincidan con los filtros seleccionados</p>
      <p v-else>Comienza agregando una nueva cita</p>
      <router-link to="/appointments/new" class="btn btn-primary mt-3">
        <i class="bi bi-plus-circle me-2"></i> Agregar Cita
      </router-link>
    </div>

    <!-- List view -->
    <div v-else-if="viewMode === 'list'">
      <div class="card">
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>
                    <a href="#" @click.prevent="sortBy('fecha')">
                      Fecha
                      <i v-if="sortField === 'fecha'" 
                         :class="sortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down'" 
                         class="bi ms-1"></i>
                    </a>
                  </th>
                  <th>Hora</th>
                  <th>Paciente</th>
                  <th>Tratamiento</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="appointment in paginatedAppointments" :key="appointment.id">
                  <td>{{ formatDate(appointment.fecha) }}</td>
                  <td>{{ formatTime(appointment.hora) }}</td>
                  <td>
                    <router-link :to="`/patients/${appointment.patient}`">
                      {{ getPatientName(appointment.patient) }}
                    </router-link>
                  </td>
                  <td>{{ appointment.tipo_tratamiento }}</td>
                  <td>
                    <span
                      :class="getStatusClass(appointment.estado)"
                      class="badge rounded-pill"
                    >
                      {{ appointment.estado }}
                    </span>
                  </td>
                  <td>
                    <div class="btn-group">
                      <router-link
                        :to="`/appointments/${appointment.id}`"
                        class="btn btn-sm btn-outline-primary"
                      >
                        <i class="bi bi-eye"></i>
                      </router-link>
                      <router-link
                        :to="`/appointments/${appointment.id}/edit`"
                        class="btn btn-sm btn-outline-secondary"
                      >
                        <i class="bi bi-pencil"></i>
                      </router-link>
                      <button 
                        class="btn btn-sm btn-outline-danger"
                        @click="confirmDeleteAppointment(appointment)"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <nav v-if="totalPages > 1" class="mt-4">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
              <i class="bi bi-chevron-left"></i>
            </a>
          </li>
          
          <li 
            v-for="page in paginationRange" 
            :key="page" 
            class="page-item"
            :class="{ active: currentPage === page }"
          >
            <a class="page-link" href="#" @click.prevent="changePage(page)">
              {{ page }}
            </a>
          </li>
          
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
              <i class="bi bi-chevron-right"></i>
            </a>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Calendar view -->
    <div v-else class="card">
      <div class="card-body">
        <AppointmentCalendar @add-appointment="navigateToNewAppointment" />
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div class="modal fade" :class="{ show: showDeleteModal, 'd-block': showDeleteModal }" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar eliminación</h5>
            <button type="button" class="btn-close" @click="showDeleteModal = false" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>¿Estás seguro que deseas eliminar la cita del paciente <strong>{{ selectedAppointment ? getPatientName(selectedAppointment.patient) : '' }}</strong> para el día <strong>{{ selectedAppointment ? formatDate(selectedAppointment.fecha) : '' }}</strong>?</p>
            <p class="text-danger">Esta acción no se puede deshacer.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="deleteAppointment" :disabled="deleting">
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Eliminar cita
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal backdrop -->
    <div v-if="showDeleteModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import { appointmentService, patientService } from '@/services/api';
import AppointmentCalendar from '@/components/appointments/AppointmentCalendar.vue';
import { formatDateForDisplay, formatTime } from '@/utils/dateUtils';

export default {
  name: 'AppointmentList',
  components: {
    AppointmentCalendar
  },
  data() {
    return {
      appointments: [],
      patients: {},
      loading: true,
      error: null,
      filters: {
        search: '',
        dateFrom: '',
        dateTo: '',
        status: '',
        patientId: null
      },
      filteredAppointments: [],
      viewMode: 'list', // 'list' or 'calendar'
      sortField: 'fecha',
      sortDirection: 'desc',
      currentPage: 1,
      itemsPerPage: 10,
      showDeleteModal: false,
      selectedAppointment: null,
      deleting: false
    };
  },
  computed: {
    hasActiveFilters() {
      return this.filters.search.trim() !== '' || 
             this.filters.dateFrom !== '' || 
             this.filters.dateTo !== '' || 
             this.filters.status !== '' ||
             this.filters.patientId !== null;
    },
    paginatedAppointments() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredAppointments.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredAppointments.length / this.itemsPerPage);
    },
    paginationRange() {
      const range = [];
      const delta = 2; // How many pages to show before and after current page
      
      for (let i = Math.max(1, this.currentPage - delta); 
           i <= Math.min(this.totalPages, this.currentPage + delta); 
           i++) {
        range.push(i);
      }
      
      return range;
    }
  },
  async created() {
    try {
      // Check if there's a patient filter in the URL
      const patientId = this.$route.query.patient;
      
      if (patientId) {
        this.filters.patientId = parseInt(patientId);
      }
      
      await this.fetchData();
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'No se pudieron cargar las citas. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchData() {
      // Fetch all appointments
      const appointmentsResponse = await appointmentService.getAll();
      this.appointments = appointmentsResponse.data;
      
      // Fetch all patients to display their names
      const patientsResponse = await patientService.getAll();
      
      // Create a map of patient ids to patient objects for easy lookup
      this.patients = patientsResponse.data.reduce((map, patient) => {
        map[patient.id] = patient;
        return map;
      }, {});
      
      // Add patient_name field to appointments
      this.appointments = this.appointments.map(appointment => {
        const patient = this.patients[appointment.patient];
        return {
          ...appointment,
          patient_name: patient ? `${patient.first_name} ${patient.last_name}` : 'Paciente desconocido'
        };
      });
      
      this.applyFilters();
    },
    
    applyFilters() {
      let filtered = [...this.appointments];
      
      // Filter by patient name (search)
      if (this.filters.search.trim()) {
        const searchTerm = this.filters.search.toLowerCase().trim();
        filtered = filtered.filter(appointment => {
          const patientName = appointment.patient_name.toLowerCase();
          return patientName.includes(searchTerm);
        });
      }
      
      // Filter by specific patient ID (from URL)
      if (this.filters.patientId) {
        filtered = filtered.filter(appointment => 
          appointment.patient === this.filters.patientId
        );
      }
      
      // Filter by date range
      if (this.filters.dateFrom) {
        const fromDate = new Date(this.filters.dateFrom);
        filtered = filtered.filter(appointment => 
          new Date(appointment.fecha) >= fromDate
        );
      }
      
      if (this.filters.dateTo) {
        const toDate = new Date(this.filters.dateTo);
        // Add one day to include the end date
        toDate.setDate(toDate.getDate() + 1);
        filtered = filtered.filter(appointment => 
          new Date(appointment.fecha) < toDate
        );
      }
      
      // Filter by status
      if (this.filters.status) {
        filtered = filtered.filter(appointment => 
          appointment.estado === this.filters.status
        );
      }
      
      // Apply sorting
      this.applySort(filtered);
      
      this.filteredAppointments = filtered;
      this.currentPage = 1; // Reset to first page when filters change
    },
    
    applySort(appointments) {
      appointments.sort((a, b) => {
        let comparison = 0;
        
        if (this.sortField === 'fecha') {
          // Sort by date and then by time
          const dateA = new Date(a.fecha + 'T' + a.hora);
          const dateB = new Date(b.fecha + 'T' + b.hora);
          comparison = dateA - dateB;
        }
        
        // Reverse if descending
        return this.sortDirection === 'asc' ? comparison : -comparison;
      });
    },
    
    sortBy(field) {
      // If already sorting by this field, toggle direction
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      
      this.applyFilters();
    },
    
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    
    getPatientName(patientId) {
      const patient = this.patients[patientId];
      return patient ? `${patient.first_name} ${patient.last_name}` : 'Paciente desconocido';
    },
    
    formatDate(dateString) {
      return formatDateForDisplay(dateString);
    },
    
    formatTime(timeString) {
      return formatTime(timeString);
    },
    
    getStatusClass(status) {
      const statusClasses = {
        'programada': 'bg-info',
        'completada': 'bg-success',
        'inasistencia': 'bg-danger',
        'confirmada': 'bg-warning'
      };
      return statusClasses[status] || 'bg-secondary';
    },
    
    confirmDeleteAppointment(appointment) {
      this.selectedAppointment = appointment;
      this.showDeleteModal = true;
    },
    
    async deleteAppointment() {
      if (!this.selectedAppointment) return;
      
      this.deleting = true;
      try {
        await appointmentService.delete(this.selectedAppointment.id);
        
        // Remove appointment from our data
        this.appointments = this.appointments.filter(a => a.id !== this.selectedAppointment.id);
        this.applyFilters();
        
        this.showDeleteModal = false;
        this.selectedAppointment = null;
        
        // Show success notification
        this.$store.dispatch('notification/success', 'Cita eliminada correctamente');
      } catch (error) {
        console.error('Error deleting appointment:', error);
        // Show error notification
        this.$store.dispatch('notification/error', 'No se pudo eliminar la cita. Por favor, intenta de nuevo.');
      } finally {
        this.deleting = false;
      }
    },
    
    navigateToNewAppointment() {
      // Navigate to new appointment page, optionally with any filters applied
      const query = {};
      if (this.filters.patientId) {
        query.patient = this.filters.patientId;
      }
      this.$router.push({ path: '/appointments/new', query });
    }
  }
};
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>